import React, { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDate } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import DataTable from "@/components/DataTable";
import StatusBadge from "@/components/StatusBadge";
import ConfirmDialog from "@/components/ConfirmDialog";
import { F } from "@/pages/Employees";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const empty = {
  nama: "", kode: "", owner: "", lokasi: "", tanggal_mulai: "", tanggal_selesai: "",
  project_manager: "", site_supervisor: "", status: "Aktif", catatan: "", anggaran_belanja_harian: 0,
};

export default function Projects() {
  const { user } = useAuth();
  const canWrite = ["super_admin", "hr", "direktur"].includes(user.role);
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [del, setDel] = useState(null);

  const load = () => api.get("/projects").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.nama) return toast.error("Nama proyek wajib diisi");
    try {
      if (editing) await api.put(`/projects/${editing}`, form);
      else await api.post("/projects", form);
      toast.success("Proyek tersimpan"); setOpen(false); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const doDelete = async () => {
    try { await api.delete(`/projects/${del.id}`); toast.success("Dihapus"); setDel(null); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const columns = [
    { key: "kode", label: "Kode", sortable: true },
    { key: "nama", label: "Nama Proyek", sortable: true, render: (r) => <span className="font-semibold">{r.nama}</span> },
    { key: "owner", label: "Owner" },
    { key: "lokasi", label: "Lokasi", sortable: true },
    { key: "project_manager", label: "Manager" },
    { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
    {
      key: "act", label: "Aksi", render: (r) => canWrite && (
        <div className="flex gap-1">
          <Button size="icon" variant="ghost" data-testid={`proj-edit-${r.id}`} onClick={() => { setForm({ ...empty, ...r }); setEditing(r.id); setOpen(true); }}><Pencil className="h-4 w-4" /></Button>
          <Button size="icon" variant="ghost" className="text-red-600" data-testid={`proj-delete-${r.id}`} onClick={() => setDel(r)}><Trash2 className="h-4 w-4" /></Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader title="Data Proyek" subtitle="Master project / lokasi kerja">
        {canWrite && <Button className="bg-blue-800 hover:bg-blue-900" data-testid="proj-add-btn" onClick={() => { setForm(empty); setEditing(null); setOpen(true); }}><Plus className="h-4 w-4 mr-1" /> Tambah Proyek</Button>}
      </PageHeader>
      <DataTable columns={columns} data={rows} searchKeys={["nama", "kode", "lokasi", "owner"]} testid="proj-table" />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit" : "Tambah"} Proyek</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <F label="Nama Proyek *"><Input data-testid="proj-f-nama" value={form.nama} onChange={(e) => setForm({ ...form, nama: e.target.value })} /></F>
            <F label="Kode (opsional)"><Input value={form.kode} onChange={(e) => setForm({ ...form, kode: e.target.value })} /></F>
            <F label="Owner / Customer"><Input value={form.owner} onChange={(e) => setForm({ ...form, owner: e.target.value })} /></F>
            <F label="Lokasi"><Input value={form.lokasi} onChange={(e) => setForm({ ...form, lokasi: e.target.value })} /></F>
            <F label="Tanggal Mulai"><Input type="date" value={form.tanggal_mulai} onChange={(e) => setForm({ ...form, tanggal_mulai: e.target.value })} /></F>
            <F label="Tanggal Selesai"><Input type="date" value={form.tanggal_selesai} onChange={(e) => setForm({ ...form, tanggal_selesai: e.target.value })} /></F>
            <F label="Project Manager"><Input value={form.project_manager} onChange={(e) => setForm({ ...form, project_manager: e.target.value })} /></F>
            <F label="Site Supervisor"><Input value={form.site_supervisor} onChange={(e) => setForm({ ...form, site_supervisor: e.target.value })} /></F>
            <F label="Target Belanja / Hari (Rp)"><Input type="number" data-testid="proj-f-anggaran" value={form.anggaran_belanja_harian} onChange={(e) => setForm({ ...form, anggaran_belanja_harian: Number(e.target.value) })} /></F>
            <F label="Status">
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{["Aktif", "Selesai", "Ditunda"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <div className="sm:col-span-2"><F label="Catatan"><Textarea value={form.catatan} onChange={(e) => setForm({ ...form, catatan: e.target.value })} /></F></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button className="bg-blue-800 hover:bg-blue-900" onClick={save} data-testid="proj-save-btn">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={!!del} onOpenChange={() => setDel(null)} destructive testid="proj-delete-dialog"
        title="Hapus Proyek?" description={`Yakin menghapus ${del?.nama}?`} confirmText="Hapus" onConfirm={doDelete} />
    </div>
  );
}
